/**
 * useProfile — Single source of truth for the current user's profile data.
 *
 * Fetches all commonly-needed profile fields in one query. Other hooks
 * (useUserPreferences, useOnboarding, useWorksheetManager) consume this
 * hook instead of querying the profiles table independently.
 *
 * TanStack Query deduplicates automatically: the first caller executes the
 * query, subsequent callers get a cache hit with zero network cost.
 */

import { useQuery } from '@tanstack/react-query';
import { User } from '@supabase/supabase-js';
import { log } from '@/lib/logger';
import { typedSelect } from '@/lib/supabaseTypedQuery';

export const PROFILE_QUERY_KEY = 'profile';

export interface ProfileData {
  // useWorksheetManager fields
  timezone: string | null;
  avatar_url: string | null;
  name: string | null;

  // useOnboarding fields
  onboarding_progress: unknown;
  onboarding_state: unknown;
  whats_new_dismissed: unknown;
  created_at: string | null;

  // useUserPreferences fields
  date_format: string | null;
  email_signature: string | null;
  phone_number: string | null;
  company_name: string | null;
  job_title: string | null;
  currency: string | null;

  // Onboarding prefill fields (reuse what we already hold)
  company_website_url: string | null;
  linkedin_profile_url: string | null;
}

const PROFILE_FIELDS = [
  'timezone',
  'avatar_url',
  'name',
  'onboarding_progress',
  'onboarding_state',
  'whats_new_dismissed',
  'created_at',
  'date_format',
  'email_signature',
  'phone_number',
  'company_name',
  'job_title',
  'currency',
  'company_website_url',
  'linkedin_profile_url',
].join(', ');

export function useProfile(user: User | null) {
  return useQuery<ProfileData | null>({
    queryKey: [PROFILE_QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await typedSelect<ProfileData>('profiles', PROFILE_FIELDS)
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        // Preserve the difference between a missing profile row and a failed
        // lookup. Timezone-sensitive writers must not turn a fetch failure into
        // a successful UTC fallback.
        log.info('Profile query failed', { code: error.code, message: error.message });
        throw error;
      }

      return data ?? null;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes — profile rarely changes mid-session
  });
}
